// src/pages/Admin/Abonnement/AbonnementPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import Button from "./../../../components/Button/Button";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import ParrainageSection from "./ParrainageSection";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const euro = (n) =>
  typeof n === "number"
    ? n.toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 })
    : n;

const fmtDate = (d) => {
  if (!d) return "—";
  // Accepte "YYYY-MM-DD" ou ISO
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`) : new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const statusBadge = (s) => {
  const key = String(s || "").toLowerCase();
  const map = {
    active: { label: "Actif", cls: "bg-emerald-600" },
    pending: { label: "En attente", cls: "bg-amber-500" },
    "in progress": { label: "En cours", cls: "bg-blue-600" },
    cancelled: { label: "Annulé", cls: "bg-rose-600" },
    canceled: { label: "Annulé", cls: "bg-rose-600" },
    expired: { label: "Expiré", cls: "bg-slate-500" },
    paused: { label: "En pause", cls: "bg-violet-600" },
  };
  return map[key] || { label: s || "—", cls: "bg-slate-500" };
};

const periodicityBadge = (p) => {
  const key = String(p || "").toLowerCase();
  const map = {
    yearly: { label: "Annuel", cls: "bg-sky-600" },
    annual: { label: "Annuel", cls: "bg-sky-600" },
    monthly: { label: "Mensuel", cls: "bg-indigo-600" },
    trimestrial: { label: "Trimestriel", cls: "bg-cyan-600" },
    quarterly: { label: "Trimestriel", cls: "bg-cyan-600" },
  };
  return map[key] || { label: p || "—", cls: "bg-slate-600" };
};

export default function AbonnementPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const ASSO_ID = process.env.REACT_APP_ASSO_ID;

  const [abonnementData, setAbonnementData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAbonnement = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `https://api.monappliclub.com/api/subscriptions/subscriptionsByAssociation/${ASSO_ID}`
      );
      setAbonnementData(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la récupération des abonnements");
    } finally {
      setLoading(false);
    }
  }, [ASSO_ID]);

  useEffect(() => {
    fetchAbonnement();
  }, [fetchAbonnement]);

  const subs = useMemo(() => abonnementData ?? [], [abonnementData]);

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* En-tête page */}
        <HeaderTitle
          role="Administrateur"
          title="Abonnement & Parrainage"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau dégradé : infos */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h3 className="text-lg sm:text-xl font-semibold text-center">
                Détails de votre abonnement
              </h3>
              <div className="mt-2 text-sm/6 max-w-3xl mx-auto text-center space-y-1">
                <p>
                  Visualisez votre <span className="font-semibold">formule</span> et la{" "}
                  <span className="font-semibold">période</span>.
                </p>
                <p>
                  Accédez à vos <span className="font-semibold">factures</span> et{" "}
                  <span className="font-semibold">renouvelez</span> si nécessaire (prochainement).
                </p>
                <p>
                  Pensez au <span className="font-semibold">parrainage</span> pour faire
                  découvrir l’application.
                </p>
              </div>
              <div className="mt-4 flex justify-center">
                <Button
                  text="Actualiser les données"
                  onClick={fetchAbonnement}
                  className="!bg-white !text-slate-900 hover:!bg-white/90 !rounded-xl !shadow-md"
                />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {/* Skeleton */}
            {loading && (
              <div className="space-y-3 mt-5">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && subs.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun abonnement trouvé
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Contactez le support si vous pensez que c’est une erreur.
                </p>
              </div>
            )}

            {/* TABLE DESKTOP */}
            {!loading && subs.length > 0 && (
              <div className="hidden md:block mt-5">
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                        {["Produit", "Montant", "Périodicité", "Début", "Fin", "Statut", "Facture"].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                      {subs.map((sub) => {
                        const s = statusBadge(sub.status);
                        const per = periodicityBadge(sub.periodicity);
                        return (
                          <tr
                            key={sub.id}
                            className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                          >
                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                              {sub.product || "—"}
                            </td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {euro(sub.total_amount)}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${per.cls}`}>
                                {per.label}
                              </span>
                            </td>
                            <td className="px-4 py-3">{fmtDate(sub.start_date)}</td>
                            <td className="px-4 py-3">{fmtDate(sub.end_date)}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${s.cls}`}>
                                {s.label}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {sub.drive_invoice_link ? (
                                <a
                                  href={sub.drive_invoice_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                                >
                                  Voir la facture ↗
                                </a>
                              ) : (
                                <span className="italic text-slate-400">Non disponible</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* CARTES MOBILE */}
            {!loading && subs.length > 0 && (
              <div className="md:hidden mt-5 space-y-3">
                {subs.map((sub) => {
                  const s = statusBadge(sub.status);
                  const per = periodicityBadge(sub.periodicity);
                  return (
                    <div
                      key={`card-${sub.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                          {sub.product || "—"}
                        </h3>
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${per.cls}`}>
                            {per.label}
                          </span>
                          <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${s.cls}`}>
                            {s.label}
                          </span>
                        </div>
                      </div>

                      <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                        <div className="flex items-center justify-between">
                          <span className="opacity-70">Montant</span>
                          <span className="font-semibold">{euro(sub.total_amount)}</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between">
                          <span className="opacity-70">Début</span>
                          <span>{fmtDate(sub.start_date)}</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between">
                          <span className="opacity-70">Fin</span>
                          <span>{fmtDate(sub.end_date)}</span>
                        </div>
                      </div>

                      <div className="mt-3">
                        {sub.drive_invoice_link ? (
                          <a
                            href={sub.drive_invoice_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            Voir la facture ↗
                          </a>
                        ) : (
                          <span className="italic text-slate-400">Facture non disponible</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Parrainage */}
        <ParrainageSection />

        <ToastContainer />
      </div>
    </section>
  );
}
